class DreiPhasenCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.drei-phasen__track');
    this.cards = Array.from(this.querySelectorAll('.drei-phasen__card'));
    this.markers = Array.from(this.querySelectorAll('.drei-phasen__marker'));
    this.fill = this.querySelector('.drei-phasen__axis-fill');

    if (!this.track || this.cards.length === 0) return;

    this.activeIndex = 0;
    this.ticking = false;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Native scroll progress tracking (works whenever native scrolling is
    // actually allowed to happen).
    this.onScroll = this.onScroll.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    // Manual pointer-driven dragging. This does NOT depend on the browser's
    // native touch-scroll gesture — it reads raw pointer coordinates and
    // writes scrollLeft directly, so it keeps working even if some other
    // script on the page calls preventDefault() on the pointerdown/touchstart
    // chain before it reaches us (which is what broke plain overflow-x
    // scrolling here — other app scripts on this store do exactly that).
    this.isDragging = false;
    this.dragPointerId = null;
    this.dragStartX = 0;
    this.dragStartScrollLeft = 0;
    this.dragMoved = false;

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerEnd = this.onPointerEnd.bind(this);

    this.track.addEventListener('pointerdown', this.onPointerDown);

    this.markers.forEach((marker, index) => {
      marker.addEventListener('click', () => this.scrollToCard(index));
    });

    this.resizeObserver = new ResizeObserver(() => {
      this.updateProgress();
      this.setupIntersectionObserver();
    });
    this.resizeObserver.observe(this.track);

    this.setupIntersectionObserver();
    this.updateProgress();
  }

  disconnectedCallback() {
    if (this.intersectionObserver) this.intersectionObserver.disconnect();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.track) {
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('pointerdown', this.onPointerDown);
    }
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerEnd);
    window.removeEventListener('pointercancel', this.onPointerEnd);
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (this.isDragging) return;

    this.isDragging = true;
    this.dragMoved = false;
    this.dragPointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.track.scrollLeft;

    window.addEventListener('pointermove', this.onPointerMove, { passive: false });
    window.addEventListener('pointerup', this.onPointerEnd, { passive: true });
    window.addEventListener('pointercancel', this.onPointerEnd, { passive: true });
  }

  onPointerMove(event) {
    if (!this.isDragging || event.pointerId !== this.dragPointerId) return;

    const delta = event.clientX - this.dragStartX;

    if (!this.dragMoved) {
      if (Math.abs(delta) < 4) return;
      this.dragMoved = true;
      this.track.classList.add('is-dragging');
    }

    // We're now committed to a horizontal drag: stop the browser from also
    // trying to do anything else with this gesture (e.g. text selection).
    event.preventDefault();
    this.track.scrollLeft = this.dragStartScrollLeft - delta;
  }

  onPointerEnd() {
    const wasDrag = this.dragMoved;
    this.isDragging = false;
    this.track.classList.remove('is-dragging');
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerEnd);
    window.removeEventListener('pointercancel', this.onPointerEnd);

    // Explicitly animate to the nearest card ourselves instead of relying on
    // the browser to re-engage scroll-snap on its own — that only fires a
    // smooth transition for gestures the browser itself tracked, not for a
    // scrollLeft we set manually frame by frame, so small drags were
    // snapping back instantly instead of gliding.
    if (wasDrag) {
      this.snapToNearestCard();
    }
  }

  snapToNearestCard() {
    const trackRect = this.track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;

    let closestIndex = this.activeIndex;
    let closestDistance = Infinity;

    this.cards.forEach((card, index) => {
      const cardRect = card.getBoundingClientRect();
      const cardCenter = cardRect.left + cardRect.width / 2;
      const distance = Math.abs(cardCenter - trackCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    this.scrollToCard(closestIndex);
  }

  setupIntersectionObserver() {
    if (this.intersectionObserver) this.intersectionObserver.disconnect();

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = this.cards.indexOf(entry.target);
            if (index > -1) this.setActive(index);
          }
        });
      },
      { root: this.track, threshold: 0.6 }
    );

    this.cards.forEach((card) => this.intersectionObserver.observe(card));
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.updateProgress();
      this.ticking = false;
    });
  }

  updateProgress() {
    if (!this.fill) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const progress = max > 0 ? this.track.scrollLeft / max : 1;
    this.fill.style.width = `${Math.min(1, Math.max(0, progress)) * 100}%`;
  }

  setActive(index) {
    this.activeIndex = index;

    this.cards.forEach((card, i) => {
      card.classList.toggle('is-active', i === index);
    });

    this.markers.forEach((marker, i) => {
      const reached = i <= index;
      marker.classList.toggle('is-reached', reached);
      if (i === index) {
        marker.setAttribute('aria-current', 'true');
      } else {
        marker.removeAttribute('aria-current');
      }
    });
  }

  scrollToCard(index) {
    const card = this.cards[index];
    if (!card) return;
    card.scrollIntoView({
      behavior: this.reduceMotion ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }
}

customElements.define('drei-phasen-carousel', DreiPhasenCarousel);
